import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppMode, type ParsedTwin } from '@repo/frontend-types';

/**
 * Whether the signed-in user's roles have loaded. Not just "metadata exists": on startup the user
 * store is restored from localStorage with the organisation but no roles, and AuthMetadataRestorer
 * fills the roles in from Auth0 afterwards. Deciding in between would treat an admin as not one.
 */
export const rolesKnownFor = (authMetadata: { roles?: unknown } | null | undefined): boolean =>
  Array.isArray(authMetadata?.roles);

/**
 * `?edit=<twin BID>` — open THAT twin in Edit mode (`twin-details-page-position-placeholder`, R5).
 *
 * Edit mode is FML's page now (spec retire-edit-mode-teardown): its one entry is Admin's Open FML
 * link (`fmlPageLink`), `/twin?spotlight=<BID>&edit=<BID>`. Onboarding's button into Edit mode,
 * which sent `/twin?tab=twin&edit=<BID>`, is gone; such an address still works the same way. This
 * hook enters Edit mode, and then takes `edit` out of the address, so a reload or Back does not
 * force Edit mode again.
 *
 * The request NAMES its twin. A request is only ever applied to that twin: if the page moves to
 * another one first (the wizard's twin was outside the signed-in organisation and never loaded, and
 * the person picked another), the request is dropped rather than landing on a twin nobody asked to
 * edit.
 *
 * A twin the page already held when the request arrived is NOT "another one". A new tab restores
 * the last twin viewed (`TwinModelStore`, from localStorage), so Admin's Open FML link
 * (`/twin?spotlight=<BID>&edit=<BID>`) arrives with a different twin selected until
 * the spotlight has switched organisation and twin. Measured on staging 2026-10-28: judging that
 * left-over twin dropped every cold-tab request as `other-twin` before the spotlight landed (spec
 * retire-edit-mode-fml-page, R5). So the left-over twin is waited through, within two bounds:
 * - only when the address's own `spotlight` names the requested twin, i.e. something is switching
 *   to it. Any other request (a bare `?edit=…`) is judged exactly as before;
 * - only until the selection first moves off the left-over twin. Coming back to it after that is a
 *   pick, and drops the request — so a load still running for the requested twin can never apply
 *   Edit while another twin is selected.
 * If the spotlight cannot switch (its organisation will not load, or the twin is refused), it says so
 * itself; the request stays in the address unapplied, is never applied to any twin but the one it
 * names, and a reload of the link tries again. Once the left-over twin's own load has settled with the
 * request still waiting, that is logged once as `[twin] edit waiting`, so a switch that never comes
 * is visible. A slow switch that then lands logs it too, so the line marks a wait, not a failure.
 *
 * WHEN, which is the whole of the difficulty. Two things set Live mode on arrival, and Edit mode must
 * land after both or it is overwritten:
 * - `TwinPage`'s load restores the twin's settings on a fresh page and sets Live, then `setTwin`s;
 * - `MainNav` moves Twin selection to Live once a twin is in context.
 * Hence the gate: this visit's own load has SETTLED for the named twin (`loadSettledFor`, set by
 * TwinPage when `loadTwinIntoContext` finishes — never the 3D scene's "models loaded", which a twin
 * left in the store by an earlier visit reports before this visit's load has set Live; measured on
 * staging 2026-10-16), that twin is in context, and the mode is no longer Twin selection. Then Edit,
 * once. A load that settled WITHOUT putting the twin in context (it failed) drops the request.
 *
 * Edit mode is a TwinLabs-admin surface, as the wizard is. Anyone else — reachable only by a typed
 * address — has the parameter removed and nothing else changes. Nothing is decided before the
 * signed-in user's metadata has loaded.
 */
export const useEditModeEntry = ({
  loadSettledFor,
  rolesKnown,
  isTlAdmin,
  twinBId,
  twin,
  appMode,
  setAppModeWithFilterReset,
}: {
  /** The twin this page visit's own load last finished for (TwinPage), or null while it runs. */
  loadSettledFor: string | null;
  /** The signed-in user's metadata has loaded — until then "not an admin" means "not known yet". */
  rolesKnown: boolean;
  isTlAdmin: boolean;
  twinBId: string | null;
  twin: ParsedTwin | null | undefined;
  appMode: AppMode;
  setAppModeWithFilterReset: (mode: AppMode) => void;
}): void => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const applied = useRef(false);
  // The twin the page held when this request was first seen, and whether the selection has moved
  // off it since (see above). Written during render, so `otherTwin` reads it in the same pass; both
  // writes are idempotent for a given render, so StrictMode's double render records the same values.
  const heldAtRequest = useRef<{
    requestedBid: string;
    twinBId: string | null;
    movedOff: boolean;
  } | null>(null);

  const params = new URLSearchParams(search);
  const requestedBid = params.get('edit');
  const requested = !!requestedBid;
  if (!requestedBid) heldAtRequest.current = null;
  else if (heldAtRequest.current?.requestedBid !== requestedBid)
    heldAtRequest.current = { requestedBid, twinBId, movedOff: false };
  else if (twinBId !== heldAtRequest.current.twinBId) heldAtRequest.current.movedOff = true;
  const switching = !!requestedBid && params.get('spotlight') === requestedBid;
  const leftOverTwin =
    switching && heldAtRequest.current && !heldAtRequest.current.movedOff
      ? heldAtRequest.current.twinBId
      : null;
  const settled = !!requestedBid && loadSettledFor === requestedBid;
  const twinInContext = !!requestedBid && twin?.adminTwin?.bid === requestedBid;
  const otherTwin =
    !!requestedBid && !!twinBId && twinBId !== requestedBid && twinBId !== leftOverTwin;
  const arrived = appMode !== AppMode.TWIN_SELECTION;
  // Waiting only when the twin held is a different one: a tab that already held the named twin is
  // not waiting for anything, and its settled load is simply applied.
  const waitingOnLeftOver =
    !!leftOverTwin && leftOverTwin !== requestedBid && loadSettledFor === leftOverTwin;
  const waitLogged = useRef<string | null>(null);

  useEffect(() => {
    if (!waitingOnLeftOver || !rolesKnown || !isTlAdmin) return;
    if (waitLogged.current === requestedBid) return;
    waitLogged.current = requestedBid;
    // eslint-disable-next-line no-console
    console.info('[twin] edit waiting', {
      reason: 'spotlight-pending',
      requestedBid,
      twinBid: leftOverTwin,
    });
  }, [waitingOnLeftOver, rolesKnown, isTlAdmin, requestedBid, leftOverTwin]);

  useEffect(() => {
    if (!requested) {
      applied.current = false;
      return;
    }
    const removeParam = () => {
      const params = new URLSearchParams(search);
      params.delete('edit');
      const rest = params.toString();
      navigate({ pathname, search: rest ? `?${rest}` : '' }, { replace: true });
    };
    const ignore = (reason: string) => {
      // eslint-disable-next-line no-console
      console.info('[twin] edit ignored', { reason, requestedBid });
      removeParam();
    };
    if (!rolesKnown) return;
    if (!isTlAdmin) return ignore('not-admin');
    if (otherTwin) return ignore('other-twin');
    if (!settled || applied.current) return;
    if (!twinInContext) return ignore('load-failed');
    if (!arrived) return;
    applied.current = true;
    setAppModeWithFilterReset(AppMode.EDIT);
    removeParam();
  }, [
    requested,
    requestedBid,
    rolesKnown,
    isTlAdmin,
    otherTwin,
    settled,
    twinInContext,
    arrived,
    pathname,
    search,
    navigate,
    setAppModeWithFilterReset,
  ]);
};
